import { Box, Slider, SliderFilledTrack, SliderThumb, SliderTrack, Text } from '@chakra-ui/react'
import { useLayoutEffect, useRef, useState } from 'react'

const VitalsSliderThumb = ({ dragLabel }: { dragLabel: string | null }) => (
  <SliderThumb h="32px" borderRadius={6} fontSize="sm" position="relative" overflow="visible">
    {dragLabel !== null ? (
      <Text
        position="absolute"
        top="calc(100% + 6px)"
        left="50%"
        bg="white"
        transform="translateX(-50%)"
        px={2}
        py={1}
        borderRadius={8}
        border="1px solid"
        borderColor="gray.300"
        fontSize="sm"
        fontWeight={600}
        fontFamily="hero-new"
        whiteSpace="nowrap"
        pointerEvents="none"
        color="gray.700"
        _dark={{ color: 'gray.100', bg: '#2a2a2a', borderColor: 'gray.600' }}
        lineHeight={1}>
        {dragLabel}
      </Text>
    ) : null}
  </SliderThumb>
)

export interface VitalsSliderProps {
  min: number
  max: number
  step: number
  /** CSS background for the track (light mode) */
  trackGradient: string
  /** CSS background for the track (dark mode) */
  trackGradientDark: string
  /** The value to display */
  value: number | undefined
  /** Formats the value shown under the thumb while dragging */
  formatValue: (value: number) => string
  /** Called when the user releases the thumb; apply rounding/clamping here if needed */
  onChangeCommitted: (value: number) => void
  /** Optional sync to a paired text input while dragging */
  onDragValue?: (displayString: string) => void
}

export const VitalsSlider = ({
  min,
  max,
  step,
  trackGradient,
  trackGradientDark,
  value,
  formatValue,
  onChangeCommitted,
  onDragValue,
}: VitalsSliderProps) => {
  const [dragLabel, setDragLabel] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  /** Chakra forces tabIndex 0 on the thumb; reset so Tab skips the slider (inputs stay in tab order). */
  useLayoutEffect(() => {
    const thumb = containerRef.current?.querySelector<HTMLElement>('[role="slider"]')
    if (thumb) thumb.tabIndex = -1
  })

  const applyDrag = (value: number) => {
    const s = formatValue(value)
    setDragLabel(s)
    onDragValue?.(s)
  }

  return (
    <Box
      ref={containerRef}
      flex={1}
      minW={0}
      overflow="visible"
      alignSelf="stretch"
      display="flex"
      alignItems="center">
      <Slider
        min={min}
        max={max}
        step={step}
        h="auto"
        py={4}
        flex={1}
        minW="0"
        onChangeStart={v => {
          setIsDragging(true)
          applyDrag(v)
        }}
        onChange={applyDrag}
        value={isDragging ? undefined : value}
        onChangeEnd={value => {
          setDragLabel(null)
          setIsDragging(false)
          onChangeCommitted(value)
        }}>
        <SliderTrack
          borderRadius="full"
          border="2px solid"
          borderColor="gray.300"
          bg={trackGradient}
          _dark={{ bg: trackGradientDark, borderColor: 'gray.600' }}
          h="24px">
          <SliderFilledTrack bg="whiteAlpha.500" />
        </SliderTrack>
        <VitalsSliderThumb dragLabel={dragLabel} />
      </Slider>
    </Box>
  )
}
